'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import { EventCard } from '@/sanity/fetchEvents';
import {
  URL_PAGE_ALWAYS,
  URL_PAGE_SOON,
  URL_PAGE_TODAY,
  URL_PATH_EVENT,
} from '@/shared/constants';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import HIHButton from '../base/Button';
import SingleArticleCard from '../base/SingleArticleCard';
import SingleArticleMobileFullViewCard from '../base/SingleArticleMobileFullviewCard';
import { SingleEventCard } from '../base/SingleEventCard';

interface ArticleOrEventRecommendationProps {
  articles?: ArticleCard[];
  events?: EventCard[];
  variant: 'articles' | 'categories' | 'events';
}

export const ArticleOrEventRecommendation = ({
  articles = [],
  events = [],
  variant,
}: ArticleOrEventRecommendationProps) => {
  const articleOrEventList = (
    isLg: boolean,
    events?: EventCard[],
    articles?: ArticleCard[],
  ) => {
    if (events && events.length > 0) {
      return events.map((event: EventCard) =>
        isLg ? (
          <div className="pb-9xl" key={event._id}>
            <SingleEventCard event={event} variant="secondary" />
          </div>
        ) : (
          <SwiperSlide key={event._id} className="!w-[320px] pb-7xl">
            <SingleEventCard event={event} variant="secondary" />
          </SwiperSlide>
        ),
      );
    } else if (articles && articles.length > 0) {
      return articles.map((article: ArticleCard) =>
        isLg ? (
          <div className="pb-9xl" key={article._id}>
            <SingleArticleCard article={article} variant="secondary" />
          </div>
        ) : (
          <SwiperSlide key={article._id} className="!w-[320px] pb-7xl">
            <SingleArticleMobileFullViewCard article={article} variant="secondary" />
          </SwiperSlide>
        ),
      );
    }
  };

  const buttonText =
    variant === 'articles'
      ? 'Mehr Guides entdecken'
      : variant === 'events'
        ? 'Mehr Events entdecken'
        : 'Heutige Tipps anschauen';
  const buttonLink =
    variant === 'articles'
      ? URL_PAGE_ALWAYS
      : variant === 'events'
        ? URL_PAGE_SOON
        : URL_PAGE_TODAY;

  return (
    <>
      {/* Desktop */}
      <div className="hidden container-content lg:flex flex-row gap-7xl pt-9xl items-start justify-start">
        <div className="flex flex-col gap-4xl min-w-[352px] max-w-[352px]">
          <div className="flex flex-col gap-lg lg:gap-4xl">
            <h2 className="font-display text-headline-lg lg:text-display-md text-sys/surface/on-surface uppercase">
              Das könnte auch was für dich sein!
            </h2>
            <p className="font-body text-title-lg text-sys/surface/on-surface-variant">
              Noch mehr Empfehlungen warten auf dich.
            </p>
          </div>
          <div className="flex">
            <HIHButton
              link={buttonLink}
              title={buttonText}
              variant="secondary"
              className="flex w-auto"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4xl">
          {articleOrEventList(true, events, articles)}
        </div>
      </div>

      {/* Mobile */}
      <div className="flex lg:hidden flex-col gap-6xl pt-7xl items-start justify-start">
        <div className="flex container-content flex-col gap-4xl">
          <div className="flex flex-col gap-lg">
            <h2 className="font-display text-headline-lg text-sys/surface/on-surface uppercase">
              Das könnte auch was für dich sein!
            </h2>
            <p className="font-body text-title-lg text-sys/surface/on-surface-variant">
              Noch mehr Empfehlungen warten auf dich.
            </p>
          </div>
          <div className="flex">
            <HIHButton
              link={buttonLink}
              title={buttonText}
              variant="secondary"
              className="flex w-auto"
            />
          </div>
        </div>
        <Swiper
          spaceBetween={16}
          slidesPerView="auto"
          scrollbar={{
            hide: false,
            draggable: true,
          }}
          className="w-full"
          slidesOffsetBefore={16}
          slidesOffsetAfter={16}
        >
          {articleOrEventList(false, events, articles)}
        </Swiper>
      </div>
    </>
  );
};

export default ArticleOrEventRecommendation;
